// Seasonal theming system - controls movies, colors, logo, and visual effects

export type ActivationMode = 'off' | 'date-range' | 'always-on';
export type ColorMode = 'solid' | 'alternating' | 'gradient';
export type EffectType = 'none' | 'snow' | 'leaves' | 'bats' | 'fireworks' | 'hearts';
export type EffectIntensity = 'light' | 'medium' | 'heavy';

export interface SeasonalLogo {
  url: string;
  fileName: string;
}

// Movie item stored in custom seasonal movie list
export interface SeasonalMovie {
  id: number;
  title: string;
  poster_path: string | null;
  backdrop_path?: string | null;
  release_date?: string;
  vote_average?: number;
}

export interface SeasonalPeriod {
  id: string;
  name: string;
  keywordId?: number; // DEPRECATED: kept for backward compatibility
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
  enabled: boolean; // Backward compatibility - maps to activationMode
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;

  // Custom movie list (replaces keyword-based discovery)
  movies?: SeasonalMovie[];

  // NEW: Theme activation control
  activationMode?: ActivationMode; // 'off' | 'date-range' | 'always-on'

  // NEW: Visual theming
  logo?: SeasonalLogo | null;
  colorMode?: ColorMode; // 'solid' | 'alternating' | 'gradient'
  colors?: string[]; // 1-5 hex color codes

  // NEW: Visual effects
  effectType?: EffectType;
  effectIntensity?: EffectIntensity;

  // NEW: Movie randomization seed (changes to rotate movies)
  movieSeed?: number;
}

// Type alias for backward compatibility
export type SeasonalTheme = SeasonalPeriod;

/**
 * Check if a date falls within a seasonal period
 * Handles year-independent date ranges including cross-year ranges (e.g., Dec 20 - Jan 5)
 */
export function isDateInRange(
  currentDate: Date,
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number
): boolean {
  const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
  const day = currentDate.getDate();

  // Create comparable numbers (MMDD format)
  const current = month * 100 + day;
  const start = startMonth * 100 + startDay;
  const end = endMonth * 100 + endDay;

  // Check if range crosses year boundary (e.g., Dec 20 - Jan 5)
  if (start > end) {
    // Range spans across year boundary
    return current >= start || current <= end;
  } else {
    // Normal range within same year
    return current >= start && current <= end;
  }
}

/**
 * Get the currently active seasonal theme based on activation mode and date
 * Priority: always-on > date-range (if in range) > off
 * Returns null if no active theme is found
 */
export async function getActiveSeasonalPeriod(): Promise<SeasonalPeriod | null> {
  try {
    // Dynamic import to work in both client and server contexts
    const { initializeApp, getApps } = await import('firebase/app');
    const { getFirestore, collection, getDocs, query, orderBy: firestoreOrderBy } = await import('firebase/firestore');

    const firebaseConfig = {
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
      authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
      storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    };

    const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    const db = getFirestore(app);

    // Fetch all themes ordered by display priority
    const periodsRef = collection(db, 'settings', 'seasonalMovies', 'periods');
    const q = query(periodsRef, firestoreOrderBy('displayOrder', 'asc'));
    const snapshot = await getDocs(q);
    const currentDate = new Date();

    let alwaysOnTheme: SeasonalPeriod | null = null;
    let dateRangeTheme: SeasonalPeriod | null = null;

    for (const doc of snapshot.docs) {
      const theme = { id: doc.id, ...doc.data() } as SeasonalPeriod;

      // Determine activation mode (backward compatibility)
      const activationMode = theme.activationMode || (theme.enabled ? 'date-range' : 'off');

      // Priority 1: Always-on theme (only one should exist)
      if (activationMode === 'always-on' && !alwaysOnTheme) {
        alwaysOnTheme = theme;
        break; // Highest priority, return immediately
      }

      // Priority 2: Date-range theme (if currently in range)
      if (activationMode === 'date-range' && !dateRangeTheme) {
        if (isDateInRange(
          currentDate,
          theme.startMonth,
          theme.startDay,
          theme.endMonth,
          theme.endDay
        )) {
          dateRangeTheme = theme;
        }
      }
    }

    // Return highest priority active theme
    return alwaysOnTheme || dateRangeTheme || null;
  } catch (error) {
    return null;
  }
}

/**
 * Default seasonal periods with TMDB keyword IDs
 */
export const DEFAULT_SEASONAL_PERIODS: Omit<SeasonalPeriod, 'id'>[] = [
  {
    name: 'Halloween',
    keywordId: 3335,
    startMonth: 10,
    startDay: 1,
    endMonth: 10,
    endDay: 31,
    enabled: true,
    displayOrder: 1,
  },
  {
    name: 'Christmas',
    keywordId: 207317,
    startMonth: 12,
    startDay: 1,
    endMonth: 12,
    endDay: 26,
    enabled: true,
    displayOrder: 2,
  },
  {
    name: "Valentine's Day",
    keywordId: 160404,
    startMonth: 2,
    startDay: 1,
    endMonth: 2,
    endDay: 14,
    enabled: true,
    displayOrder: 3,
  },
  {
    name: 'Thanksgiving',
    keywordId: 4543,
    startMonth: 11,
    startDay: 1,
    endMonth: 11,
    endDay: 30,
    enabled: true,
    displayOrder: 4,
  },
  {
    name: "New Year's Eve",
    keywordId: 613,
    startMonth: 12,
    startDay: 26,
    endMonth: 1,
    endDay: 5,
    enabled: true,
    displayOrder: 5,
  },
  {
    name: 'Independence Day',
    keywordId: 235503,
    startMonth: 6,
    startDay: 15,
    endMonth: 7,
    endDay: 10,
    enabled: true,
    displayOrder: 6,
  },
];

/**
 * Get month name from number
 */
export function getMonthName(month: number): string {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return months[month - 1] || '';
}

/**
 * Format date range for display
 */
export function formatDateRange(
  startMonth: number,
  startDay: number,
  endMonth: number,
  endDay: number
): string {
  const startMonthName = getMonthName(startMonth);
  const endMonthName = getMonthName(endMonth);

  if (startMonth === endMonth) {
    return `${startMonthName} ${startDay}-${endDay}`;
  } else {
    return `${startMonthName} ${startDay} - ${endMonthName} ${endDay}`;
  }
}

/**
 * Shuffle array using Fisher-Yates algorithm with a seed for deterministic results
 */
export function shuffleWithSeed<T>(array: T[], seed: number): T[] {
  const shuffled = [...array];
  let currentSeed = seed;

  // Simple seeded random number generator
  const seededRandom = () => {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    return currentSeed / 233280;
  };

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * Generate a daily seed based on theme seed and current date
 * Returns same value for entire day, changes at midnight
 */
export function getDailySeed(themeSeed: number = 0): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);

  return themeSeed + dayOfYear;
}

/**
 * Get activation mode label for display
 */
export function getActivationModeLabel(mode: ActivationMode): string {
  switch (mode) {
    case 'off': return 'Disabled';
    case 'date-range': return 'Date Range';
    case 'always-on': return 'Always On';
    default: return 'Unknown';
  }
}

/**
 * Get color mode label for display
 */
export function getColorModeLabel(mode: ColorMode): string {
  switch (mode) {
    case 'solid': return 'Solid Color';
    case 'alternating': return 'Alternating Colors';
    case 'gradient': return 'Gradient';
    default: return 'None';
  }
}

/**
 * Get effect type label for display
 */
export function getEffectTypeLabel(type: EffectType): string {
  switch (type) {
    case 'none': return 'None';
    case 'snow': return 'Snowfall';
    case 'leaves': return 'Falling Leaves';
    case 'bats': return 'Flying Bats';
    case 'fireworks': return 'Fireworks';
    case 'hearts': return 'Floating Hearts';
    default: return 'None';
  }
}
